import { getApiBase, isOpsPage, isDeveloperConsolePage } from './api-boundary';

function formatApiErrorMessage(status: number, data: any): string {
    if (data && typeof data === 'object') {
        const detail = typeof data.error === 'string' && data.error.trim()
            ? data.error.trim()
            : typeof data.message === 'string' && data.message.trim()
                ? data.message.trim()
                : null;
        if (detail) {
            // Guard against accidentally logging sensitive credentials
            const sanitized = detail.replace(/(?:token|secret|password|code|otp)=\S+/gi, '[REDACTED]');
            return `API Error: ${status} - ${sanitized}`;
        }
    }
    return `API Error: ${status}`;
}

export class ApiError extends Error {
    constructor(public status: number, public data: any) {
        super(formatApiErrorMessage(status, data));
        this.name = 'ApiError';
    }
}

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

async function tryRefreshToken(): Promise<boolean> {
    if (isRefreshing) return refreshPromise!;
    isRefreshing = true;
    refreshPromise = fetch('/api/auth/refresh', { method: 'POST', signal: AbortSignal.timeout(10_000) })
        .then(r => r.ok)
        .catch(() => false)
        .finally(() => { isRefreshing = false; refreshPromise = null; });
    return refreshPromise;
}

async function request<T>(
    path: string,
    options: RequestInit = {},
    isRetry = false
): Promise<T> {
    const devConsoleRequest = isDeveloperConsolePage();
    const opsRequest = isOpsPage();
    const url = `${getApiBase()}${path}`;

    const method = (options.method ?? 'GET').toUpperCase();
    const defaultHeaders: Record<string, string> = {};
    if (devConsoleRequest) {
        defaultHeaders['x-developer-console-csrf'] = '1';
        defaultHeaders['X-Ops-CSRF'] = '1';
    } else if (opsRequest) {
        defaultHeaders['X-Ops-CSRF'] = '1';
    }
    if (method !== 'GET' && method !== 'HEAD' && method !== 'DELETE') {
        defaultHeaders['Content-Type'] = 'application/json';
    }

    const headers = new Headers(defaultHeaders);
    new Headers(options.headers).forEach((value, key) => headers.set(key, value));
    const response = await fetch(url, {
        ...options,
        headers,
    });

    if (!response.ok) {
        let errorData;
        try {
            errorData = await response.json();
        } catch {
            errorData = { message: 'An unknown error occurred' };
        }

        let skipAuthRedirect = false;
        if (options.headers) {
            if (options.headers instanceof Headers) {
                skipAuthRedirect = options.headers.get('X-Skip-Auth-Redirect') === 'true';
            } else if (Array.isArray(options.headers)) {
                skipAuthRedirect = options.headers.some(([k, v]) => k.toLowerCase() === 'x-skip-auth-redirect' && v === 'true');
            } else {
                const h = options.headers as Record<string, string>;
                skipAuthRedirect = Object.keys(h).some(k => k.toLowerCase() === 'x-skip-auth-redirect' && h[k] === 'true');
            }
        }

        // On 401: attempt one silent token refresh then retry the original request.
        // If refresh fails, force logout so the user lands on the login page cleanly.
        if (response.status === 401 && devConsoleRequest) {
            const redirect = encodeURIComponent(window.location.pathname + window.location.search);
            window.location.assign(`/developer-console/login?redirect=${redirect}`);
            throw new ApiError(401, errorData);
        }
        if (response.status === 401 && opsRequest) {
            window.location.assign('/ops/login');
            throw new ApiError(401, errorData);
        }
        if (response.status === 401 && !isRetry && typeof window !== 'undefined' && !skipAuthRedirect) {
            const refreshed = await tryRefreshToken();
            if (refreshed) return request<T>(path, options, true);

            // Refresh token expired or invalid — sign out
            const { signOut } = await import('@/lib/auth');
            await signOut();
            throw new ApiError(401, errorData);
        }

        // Detect plan-gated 403s and fire upgrade prompt event
        if (response.status === 403 && errorData.code === 'FEATURE_NOT_ENTITLED') {
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('plan-gate', {
                    detail: { feature: errorData.feature }
                }));
            }
        }

        throw new ApiError(response.status, errorData);
    }

    if (response.status === 204) {
        return {} as T;
    }

    return response.json();
}

export const api = {
    get: <T>(path: string, options?: RequestInit) =>
        request<T>(path, { ...options, method: 'GET' }),

    post: <T>(path: string, data?: any, options?: RequestInit) =>
        request<T>(path, {
            ...options,
            method: 'POST',
            body: JSON.stringify(data)
        }),

    put: <T>(path: string, data?: any, options?: RequestInit) =>
        request<T>(path, {
            ...options,
            method: 'PUT',
            body: JSON.stringify(data)
        }),

    patch: <T>(path: string, data?: any, options?: RequestInit) =>
        request<T>(path, {
            ...options,
            method: 'PATCH',
            body: JSON.stringify(data)
        }),

    del: <T>(path: string, options?: RequestInit) =>
        request<T>(path, { ...options, method: 'DELETE' }),

    delete: <T>(path: string, options?: RequestInit) =>
        request<T>(path, { ...options, method: 'DELETE' }),
};
